import React, { useState, useEffect } from "react";
import {
  Calculator,
  Target,
  Clock,
  Coins,
  TrendUp,
  Calendar,
  Lightbulb,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { formatCurrency } from "../utils/calculations";
import LifestyleSlider from "../components/LifestyleSlider";

type FIREType = "traditional" | "coast" | "fat" | "barista" | "lean";

interface FIREInputs {
  currentAge: number;
  currentSavings: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  expectedReturn: number;
  inflationRate: number;
  withdrawalRate: number;
  additionalIncome?: number;
  additionalExpenses?: number;
  fireType: FIREType;
}

interface FIREResults {
  targetAmount: number;
  yearsToFI: number;
  monthlySavingsNeeded: number;
  projectedSavings: number;
  projectedExpenses: number;
  fireAge: number;
  monthlySurplus: number;
  savingsRate: number;
  timeline: Array<{
    year: number;
    age: number;
    savings: number;
    expenses: number;
    fireAmount: number;
  }>;
}

const FIRE: React.FC = () => {
  const { getDashboardData, getCurrentUser } = useData();
  const dashboardData = getDashboardData();
  const currentUser = getCurrentUser();

  // Load saved FIRE data from localStorage or use defaults
  const loadFIREInputs = (): FIREInputs => {
    const saved = localStorage.getItem("fire-calculator-inputs");
    if (saved) {
      return JSON.parse(saved);
    }

    // Default values based on current dashboard data
    // For FIRE calculations, use total assets (not just liquid assets)
    const totalAssets =
      dashboardData?.assets?.reduce((sum: number, asset: any) => {
        const balance =
          typeof asset.balance === "string"
            ? parseFloat(asset.balance)
            : asset.balance;
        return sum + (balance || 0);
      }, 0) || 0;

    return {
      currentAge: currentUser?.age || 30,
      currentSavings: totalAssets || dashboardData?.currentBalance || 0,
      monthlyIncome: dashboardData?.monthlyIncome || 0,
      monthlyExpenses: dashboardData?.monthlyExpenses || 0,
      expectedReturn: 7,
      inflationRate: 2.5,
      withdrawalRate: 4,
      additionalIncome: 0,
      additionalExpenses: 0,
      fireType: "traditional" as FIREType,
    };
  };

  const [inputs, setInputs] = useState<FIREInputs>(loadFIREInputs);
  const [results, setResults] = useState<FIREResults | null>(null);
  const [activeTab, setActiveTab] = useState<
    "calculator" | "timeline" | "insights"
  >("calculator");

  // Save inputs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("fire-calculator-inputs", JSON.stringify(inputs));
  }, [inputs]);

  const calculateFIRE = React.useCallback((): FIREResults => {
    const {
      currentAge,
      currentSavings,
      monthlyIncome,
      monthlyExpenses,
      expectedReturn,
      inflationRate,
      withdrawalRate,
      additionalIncome = 0,
      additionalExpenses = 0,
    } = inputs;

    const adjustedMonthlyIncome = monthlyIncome + additionalIncome;
    const adjustedMonthlyExpenses = monthlyExpenses + additionalExpenses;
    const monthlySurplus = adjustedMonthlyIncome - adjustedMonthlyExpenses;
    const savingsRate = (monthlySurplus / adjustedMonthlyIncome) * 100;

    // Calculate target FIRE amount based on FIRE type
    const annualExpenses = adjustedMonthlyExpenses * 12;

    const getTargetAmount = (fireType: FIREType, baseExpenses: number) => {
      switch (fireType) {
        case "traditional":
          return baseExpenses / (withdrawalRate / 100);
        case "coast":
          // Coast FIRE: enough to cover expenses until traditional retirement age
          const yearsToTraditionalRetirement = 65 - currentAge;
          return baseExpenses * yearsToTraditionalRetirement;
        case "fat":
          // Fat FIRE: 3x traditional amount for luxury lifestyle
          return (baseExpenses / (withdrawalRate / 100)) * 3;
        case "barista":
          // Barista FIRE: 70% of traditional amount (part-time work covers 30%)
          return (baseExpenses / (withdrawalRate / 100)) * 0.7;
        case "lean":
          // Lean FIRE: 50% of traditional amount for minimal lifestyle
          return (baseExpenses / (withdrawalRate / 100)) * 0.5;
        default:
          return baseExpenses / (withdrawalRate / 100);
      }
    };

    const targetAmount = getTargetAmount(inputs.fireType, annualExpenses);

    // Separate cash (liquid assets) from investments
    const liquidAssets =
      dashboardData?.assets
        ?.filter((asset: any) => asset.isLiquid)
        ?.reduce((sum: number, asset: any) => {
          const balance =
            typeof asset.balance === "string"
              ? parseFloat(asset.balance)
              : asset.balance;
          return sum + (balance || 0);
        }, 0) || 0;

    const investmentAssets =
      dashboardData?.assets
        ?.filter((asset: any) => !asset.isLiquid)
        ?.reduce((sum: number, asset: any) => {
          const balance =
            typeof asset.balance === "string"
              ? parseFloat(asset.balance)
              : asset.balance;
          return sum + (balance || 0);
        }, 0) || 0;

    // Cash doesn't get investment returns, only investments do
    const totalAssets = liquidAssets + investmentAssets;
    const annualRate = expectedReturn / 100;
    const annualSavings = monthlySurplus * 12;

    let yearsToFI = 0;

    if (totalAssets >= targetAmount) {
      yearsToFI = 0; // Already FI
    } else if (annualSavings > 0 && annualRate > 0) {
      // FIRE calculation with compound growth
      // Use the compound interest formula: FV = PV * (1 + r)^n + PMT * ((1 + r)^n - 1) / r
      // Where: FV = targetAmount, PV = totalAssets, PMT = annualSavings, r = annualRate, n = yearsToFI
      // Solving for n using iterative approach or approximation

      // For simplicity, we'll use an iterative approach
      let years = 0;
      let currentAssets = totalAssets;

      while (currentAssets < targetAmount && years < 100) {
        currentAssets = currentAssets * (1 + annualRate) + annualSavings;
        years++;
      }

      if (currentAssets >= targetAmount) {
        yearsToFI = years;
      } else {
        yearsToFI = Infinity; // Can't reach FI within reasonable time
      }
    } else if (annualSavings > 0) {
      // No investment returns, just savings
      yearsToFI = (targetAmount - totalAssets) / annualSavings;
    } else {
      // No savings, can't reach FI
      yearsToFI = Infinity;
    }

    const fireAge = currentAge + yearsToFI;

    // Calculate monthly savings needed
    // If already FI, no additional savings needed
    // If can't reach FI, use current surplus
    // Otherwise, calculate the monthly savings needed to reach FI in the calculated years
    let monthlySavingsNeeded = monthlySurplus;

    if (yearsToFI > 0 && yearsToFI < Infinity) {
      // Calculate required monthly savings using compound interest formula
      // FV = PMT * ((1+r)^n - 1) / r + PV * (1+r)^n
      // Solving for PMT: PMT = (FV - PV * (1+r)^n) / ((1+r)^n - 1) / r
      const annualRate = expectedReturn / 100;
      const futureValue = targetAmount;
      const presentValue = currentSavings;
      const years = yearsToFI;

      const futureValueOfPresent =
        presentValue * Math.pow(1 + annualRate, years);
      const requiredFutureValue = futureValue - futureValueOfPresent;

      if (requiredFutureValue > 0) {
        const monthlyRate = annualRate / 12;
        const months = years * 12;
        const denominator =
          (Math.pow(1 + monthlyRate, months) - 1) / monthlyRate;

        if (denominator > 0) {
          monthlySavingsNeeded = requiredFutureValue / denominator;
        }
      } else {
        monthlySavingsNeeded = 0; // Already have enough
      }
    }

    // Generate timeline
    const timeline = [];
    let projectedCash = liquidAssets;
    let projectedInvestments = investmentAssets;
    let projectedExpenses = annualExpenses;

    for (let year = 0; year <= Math.min(Math.ceil(yearsToFI) + 5, 30); year++) {
      const age = currentAge + year;
      const fireAmount = projectedExpenses / (withdrawalRate / 100);
      const projectedSavings = projectedCash + projectedInvestments;

      timeline.push({
        year,
        age,
        savings: projectedSavings,
        expenses: projectedExpenses,
        fireAmount,
      });

      // Update for next year
      // All assets grow at the investment rate, and we add annual savings
      const totalProjectedAssets = projectedCash + projectedInvestments;
      const newTotalAssets =
        totalProjectedAssets * (1 + annualRate) + annualSavings;

      // Distribute growth proportionally between cash and investments
      if (totalProjectedAssets > 0) {
        const cashRatio = projectedCash / totalProjectedAssets;
        const investmentRatio = projectedInvestments / totalProjectedAssets;

        projectedCash = newTotalAssets * cashRatio;
        projectedInvestments = newTotalAssets * investmentRatio;
      } else {
        // If no existing assets, put all new money in investments
        projectedCash = 0;
        projectedInvestments = newTotalAssets;
      }

      projectedExpenses = projectedExpenses * (1 + inflationRate / 100);
    }

    return {
      targetAmount,
      yearsToFI: Math.max(0, yearsToFI),
      monthlySavingsNeeded,
      projectedSavings: projectedCash + projectedInvestments,
      projectedExpenses,
      fireAge,
      monthlySurplus,
      savingsRate,
      timeline,
    };
  }, [inputs]);

  useEffect(() => {
    setResults(calculateFIRE());
  }, [calculateFIRE]);

  const handleInputChange = (
    field: keyof FIREInputs,
    value: number | FIREType
  ) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const handleDollarInputChange = (field: keyof FIREInputs, value: string) => {
    // Remove any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, "");
    // Convert to number, defaulting to 0 if empty
    const numValue = cleanValue === "" ? 0 : parseFloat(cleanValue) || 0;
    setInputs((prev) => ({ ...prev, [field]: numValue }));
  };

  const formatDollarValue = (value: number): string => {
    if (value === 0) return "";
    return `$${value.toString()}`;
  };

  const formatPercentageValue = (value: number): string => {
    if (value === 0) return "";
    return value.toString();
  };

  const resetToDashboardData = () => {
    if (dashboardData) {
      // For FIRE calculations, use total assets (not just liquid assets)
      const totalAssets =
        dashboardData.assets?.reduce((sum: number, asset: any) => {
          const balance =
            typeof asset.balance === "string"
              ? parseFloat(asset.balance)
              : asset.balance;
          return sum + (balance || 0);
        }, 0) || 0;

      setInputs({
        currentAge: currentUser?.age || 30,
        currentSavings: totalAssets || dashboardData.currentBalance,
        monthlyIncome: dashboardData.monthlyIncome,
        monthlyExpenses: dashboardData.monthlyExpenses,
        expectedReturn: 7,
        inflationRate: 2.5,
        withdrawalRate: 4,
        additionalIncome: 0,
        additionalExpenses: 0,
        fireType: "traditional" as FIREType,
      });
    }
  };

  const getFireStatus = () => {
    if (!results)
      return {
        status: "calculating",
        message: "Calculating...",
        color: "text-gray-500",
        bgColor: "bg-gray-100 dark:bg-gray-700",
        borderColor: "border-gray-300 dark:border-gray-600",
      };

    if (results.yearsToFI <= 0) {
      return {
        status: "fire",
        message: "🎉 You've reached FIRE!",
        color: "text-primary-500 dark:text-primary-400",
        bgColor: "bg-primary-50 dark:bg-primary-900/20",
        borderColor: "border-primary-300 dark:border-primary-600",
      };
    } else if (results.yearsToFI <= 5) {
      return {
        status: "close",
        message: "🚀 Almost there! Keep going!",
        color: "text-purple-500 dark:text-purple-400",
        bgColor: "bg-purple-50 dark:bg-purple-900/20",
        borderColor: "border-purple-300 dark:border-purple-600",
      };
    } else if (results.yearsToFI <= 10) {
      return {
        status: "on-track",
        message: "📈 On track to FIRE",
        color: "text-teal-500 dark:text-teal-400",
        bgColor: "bg-teal-50 dark:bg-teal-900/20",
        borderColor: "border-teal-300 dark:border-teal-600",
      };
    } else {
      return {
        status: "far",
        message: "🎯 Long way to go, but you can do it!",
        color: "text-gray-600 dark:text-gray-400",
        bgColor: "bg-gray-50 dark:bg-gray-800/20",
        borderColor: "border-gray-300 dark:border-gray-600",
      };
    }
  };

  const fireStatus = getFireStatus();

  if (!dashboardData) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              FIRE Calculator
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please complete your setup to use the FIRE calculator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div></div>
        <button
          onClick={resetToDashboardData}
          className="btn-secondary flex items-center space-x-2"
          title="Reset to current dashboard data">
          <Clock className="w-4 h-4" weight="duotone" />
          <span>Reset to Dashboard</span>
        </button>
      </div>

      {/* Combined FIRE Status and Strategy Card */}
      {results && (
        <div className="card">
          {/* Strategy Description */}
          <div className="mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>FIRE Strategy:</strong>{" "}
              {inputs.fireType === "traditional" &&
                "Traditional FIRE uses the 4% rule - you need 25x your annual expenses to retire safely."}
              {inputs.fireType === "coast" &&
                "Coast FIRE means you have enough saved to cover expenses until traditional retirement age (65) without additional contributions."}
              {inputs.fireType === "fat" &&
                "Fat FIRE targets 3x the traditional amount for a luxury lifestyle with travel, dining, and premium experiences."}
              {inputs.fireType === "barista" &&
                "Barista FIRE requires 70% of traditional FIRE amount, with part-time work covering the remaining 30% of expenses."}
              {inputs.fireType === "lean" &&
                "Lean FIRE targets 50% of traditional FIRE amount for a minimal lifestyle with reduced expenses and simple living."}
            </div>
          </div>

          {/* Strategy Selector */}
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              FIRE Strategy
            </label>
            <div className="flex space-x-1">
              {[
                {
                  key: "traditional",
                  label: "Traditional",
                  description: "4% rule",
                },
                {
                  key: "coast",
                  label: "Coast",
                  description: "Cover until 65",
                },
                {
                  key: "fat",
                  label: "Fat",
                  description: "Luxury",
                },
                {
                  key: "barista",
                  label: "Barista",
                  description: "Part-time",
                },
                {
                  key: "lean",
                  label: "Lean",
                  description: "Minimal",
                },
              ].map((type) => (
                <button
                  key={type.key}
                  onClick={() =>
                    handleInputChange("fireType", type.key as FIREType)
                  }
                  className={`px-3 py-1.5 rounded-lg border text-xs transition-all duration-200 ${
                    inputs.fireType === type.key
                      ? "border-primary-500 bg-primary-100 dark:bg-primary-900/30 text-teal-900 dark:text-primary-200"
                      : "border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600"
                  }`}>
                  <div className="font-medium">{type.label}</div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {type.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Status Section */}
          <div
            className={`p-4 rounded-lg border-2 ${fireStatus.borderColor} ${fireStatus.bgColor} transition-all duration-300`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Target
                  className={`w-6 h-6 ${fireStatus.color}`}
                  weight="duotone"
                />
                <div>
                  <span className={`font-semibold text-lg ${fireStatus.color}`}>
                    {fireStatus.message}
                  </span>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Target: {formatCurrency(results.targetAmount)} • FIRE Age:{" "}
                    {results.fireAge.toFixed(1)} • Years to FI:{" "}
                    {results.yearsToFI.toFixed(1)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${fireStatus.color}`}>
                  {results.yearsToFI <= 0
                    ? "🎉"
                    : results.yearsToFI <= 5
                    ? "🚀"
                    : results.yearsToFI <= 10
                    ? "📈"
                    : "🎯"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {results.savingsRate.toFixed(1)}% savings rate
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Calculator */}
      <div className="card">
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-1 mb-6">
          {[
            { key: "calculator", label: "Calculator", icon: Calculator },
            { key: "timeline", label: "Timeline", icon: Calendar },
            { key: "insights", label: "Insights", icon: Lightbulb },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center justify-center sm:justify-start space-x-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeTab === tab.key
                  ? "bg-primary-500 text-teal-900 font-semibold"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
              }`}>
              <tab.icon className="w-4 h-4" weight="duotone" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Calculator Tab */}
        {activeTab === "calculator" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Inputs */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Your Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Current Age
                  </label>
                  <input
                    type="number"
                    value={inputs.currentAge}
                    onChange={(e) =>
                      handleInputChange("currentAge", Number(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Current Savings
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Includes all assets (retirement, investments, savings, etc.)
                  </p>
                  {dashboardData?.assets && dashboardData.assets.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <span className="font-medium">Current Assets:</span>{" "}
                      {dashboardData.assets.length} account
                      {dashboardData.assets.length !== 1
                        ? "s"
                        : ""} totaling{" "}
                      {formatCurrency(
                        dashboardData.assets.reduce(
                          (sum: number, asset: any) => {
                            const balance =
                              typeof asset.balance === "string"
                                ? parseFloat(asset.balance)
                                : asset.balance;
                            return sum + (balance || 0);
                          },
                          0
                        )
                      )}
                      <br />
                      <span className="text-xs">
                        💰 Cash:{" "}
                        {formatCurrency(
                          dashboardData.assets
                            .filter((asset: any) => asset.isLiquid)
                            .reduce((sum: number, asset: any) => {
                              const balance =
                                typeof asset.balance === "string"
                                  ? parseFloat(asset.balance)
                                  : asset.balance;
                              return sum + (balance || 0);
                            }, 0)
                        )}{" "}
                        | 📈 Investments:{" "}
                        {formatCurrency(
                          dashboardData.assets
                            .filter((asset: any) => !asset.isLiquid)
                            .reduce((sum: number, asset: any) => {
                              const balance =
                                typeof asset.balance === "string"
                                  ? parseFloat(asset.balance)
                                  : asset.balance;
                              return sum + (balance || 0);
                            }, 0)
                        )}
                      </span>
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="$0"
                    value={formatDollarValue(inputs.currentSavings)}
                    onChange={(e) =>
                      handleDollarInputChange("currentSavings", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monthly Income
                  </label>
                  <input
                    type="text"
                    placeholder="$0"
                    value={formatDollarValue(inputs.monthlyIncome)}
                    onChange={(e) =>
                      handleDollarInputChange("monthlyIncome", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monthly Expenses
                  </label>
                  <input
                    type="text"
                    placeholder="$0"
                    value={formatDollarValue(inputs.monthlyExpenses)}
                    onChange={(e) =>
                      handleDollarInputChange("monthlyExpenses", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Assumptions */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Assumptions
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expected Annual Return (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formatPercentageValue(inputs.expectedReturn)}
                    onChange={(e) =>
                      handleInputChange(
                        "expectedReturn",
                        Number(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Inflation Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formatPercentageValue(inputs.inflationRate)}
                    onChange={(e) =>
                      handleInputChange("inflationRate", Number(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Withdrawal Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formatPercentageValue(inputs.withdrawalRate)}
                    onChange={(e) =>
                      handleInputChange(
                        "withdrawalRate",
                        Number(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Additional Monthly Income
                  </label>
                  <input
                    type="text"
                    placeholder="$0"
                    value={formatDollarValue(inputs.additionalIncome || 0)}
                    onChange={(e) =>
                      handleDollarInputChange(
                        "additionalIncome",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Additional Monthly Expenses
                  </label>
                  <input
                    type="text"
                    placeholder="$0"
                    value={formatDollarValue(inputs.additionalExpenses || 0)}
                    onChange={(e) =>
                      handleDollarInputChange(
                        "additionalExpenses",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-5 h-5 text-primary-500" weight="duotone" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Target Amount
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(results.targetAmount)}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-blue-500" weight="duotone" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Years to FIRE
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {results.yearsToFI.toFixed(1)}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center space-x-2 mb-2">
                <Coins className="w-5 h-5 text-green-500" weight="duotone" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Monthly Savings
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(results.monthlySavingsNeeded)}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center space-x-2 mb-2">
                <TrendUp className="w-5 h-5 text-yellow-500" weight="duotone" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Savings Rate
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {results.savingsRate.toFixed(1)}%
              </p>
            </div>
          </div>
        )}

        {/* Spending Lifestyle Reference - Hidden for now */}
        {/* 
        <div className="mt-6 card">
          <div className="flex items-center space-x-2 mb-4">
            <Lightbulb className="w-5 h-5 text-yellow-500" weight="duotone" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Spending Lifestyle Guide
            </h3>
          </div>
          <LifestyleSlider
            currentTarget={results?.targetAmount}
            onLifestyleChange={(lifestyle) => {
              // Optional: You could update the FIRE calculation based on the selected lifestyle
              console.log("Selected lifestyle:", lifestyle);
            }}
          />
        </div>
        */}

        {/* Timeline Tab */}
        {activeTab === "timeline" && results && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              FIRE Timeline
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 text-gray-600 dark:text-gray-400">
                      Year
                    </th>
                    <th className="text-left py-2 text-gray-600 dark:text-gray-400">
                      Age
                    </th>
                    <th className="text-right py-2 text-gray-600 dark:text-gray-400">
                      Savings
                    </th>
                    <th className="text-right py-2 text-gray-600 dark:text-gray-400">
                      Expenses
                    </th>
                    <th className="text-right py-2 text-gray-600 dark:text-gray-400">
                      FIRE Amount
                    </th>
                    <th className="text-center py-2 text-gray-600 dark:text-gray-400">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.timeline.slice(0, 15).map((year) => (
                    <tr
                      key={year.year}
                      className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 text-gray-900 dark:text-white">
                        {year.year}
                      </td>
                      <td className="py-2 text-gray-900 dark:text-white">
                        {year.age}
                      </td>
                      <td className="py-2 text-right text-gray-900 dark:text-white">
                        {formatCurrency(year.savings)}
                      </td>
                      <td className="py-2 text-right text-gray-900 dark:text-white">
                        {formatCurrency(year.expenses)}
                      </td>
                      <td className="py-2 text-right text-gray-900 dark:text-white">
                        {formatCurrency(year.fireAmount)}
                      </td>
                      <td className="py-2 text-center">
                        {year.savings >= year.fireAmount ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                            FIRE
                          </span>
                        ) : year.year <= results.yearsToFI ? (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                            On Track
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full">
                            Planning
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Insights Tab */}
        {activeTab === "insights" && results && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              FIRE Insights
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Current Status
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Monthly Surplus:
                    </span>
                    <span
                      className={`font-medium ${
                        results.monthlySurplus >= 0
                          ? "text-green-500"
                          : "text-red-500"
                      }`}>
                      {formatCurrency(results.monthlySurplus)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Savings Rate:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {results.savingsRate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Years to FIRE:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {results.yearsToFI.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Recommendations
                </h4>
                <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  {results.savingsRate < 50 && (
                    <p>💡 Increase your savings rate to 50%+ for faster FIRE</p>
                  )}
                  {results.monthlySavingsNeeded > results.monthlySurplus && (
                    <p>
                      ⚠️ You need to save more or increase income to reach FIRE
                    </p>
                  )}
                  {results.yearsToFI > 20 && (
                    <p>🎯 Consider increasing income or reducing expenses</p>
                  )}
                  {results.savingsRate >= 70 && (
                    <p>
                      🚀 Excellent savings rate! You're on the fast track to
                      FIRE
                    </p>
                  )}
                  <p>
                    📊 The 4% rule suggests you can safely withdraw 4% annually
                    in retirement
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                FIRE Strategies
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                    Increase Income
                  </h5>
                  <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                    <li>• Ask for raises/promotions</li>
                    <li>• Start a side hustle</li>
                    <li>• Invest in skills</li>
                    <li>• Switch to higher-paying job</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                    Reduce Expenses
                  </h5>
                  <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                    <li>• Cut unnecessary subscriptions</li>
                    <li>• Downsize housing</li>
                    <li>• Cook at home more</li>
                    <li>• Use public transportation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FIRE;
